<script>
   /*************************************************************/
   /* PlotPredictions                                           */
   /* --------------------                                      */
   /* shows plot with predicted vs measured y-values            */
   /*************************************************************/

   import { Axes, XAxis, Box, Lines, Rectangles, ColormapLegend } from 'svelte-plots-basic/2d';
   import { Vector } from 'mdatools/arrays';
   import { mrange } from 'mdatools/stat';

   import { MARKERS, PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS } from '@mdatools-apps/shared/plots/Defaults.js';
   import PlotSeries from '@mdatools-apps/shared/plots/PlotSeries.svelte';
   import { plotSize, legendNames } from '@mdatools-apps/shared/plots/layout.js';
   import { csvRow, downloadCSV } from '@mdatools-apps/shared/utils';
   import { getClasses } from './Controls.svelte.js';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();


   // get the properties needed for the plot
   const model = $derived(props ? props.classmodel : null)
   const res = $derived(props ? props.classres : null);
   const resIDs = $derived(res ? Object.keys(res) : null);
   const resobj = $derived(params.resID && resIDs ? res[params.resID] : null);
   const classNames = $derived(resobj ? resobj.classNames : null);
   const nObj = $derived(resobj ? resobj.nrows : 1);

   // define colors for different selection options (first color - class 1, second - class 2)
   const CLASS_COLORS_SET = $derived.by(() => {
      if (!classNames) return null;
      const out = {};
      out[classNames[0]] = ["#7b3294", "#a6dba0"];
      out[classNames[1]] = ["#c2a5cf", "#008837"];
      out["both"]        = ["#7b3294", "#008837"];
      return out;
   });

   const CLASS_COLORS = $derived(CLASS_COLORS_SET ? CLASS_COLORS_SET[params.className] : null);


   // plot parameters
   const title = $derived(props.ncomp ? 'Discrimination (A = ' + props.ncomp + ')' : '');

   // elements of plot series
   const labels = $derived(resobj ? resobj.objlabels : null);

   // axes limits
   const limY = [0, 3];

   // distribution curves for soft rule
   const dist = $derived.by(() => {
      if (!model || model.rule === 'hard' || !model.crit) return null;

      const out = {};
      let cnum = 0;
      for (let c of model.classNames) {
         out[c] = {
            xValues: props.zscores.apply(v => v * model.crit[c][1] + model.crit[c][0]),
            yValues: props.densnorm.apply(v => v + cnum + 1.05),
            lineColor: CLASS_COLORS[cnum]
         }
         cnum += 1;
      }
      return out;
   });

   function getPoints(ncomp, resobj) {

      const n = resobj.nrows;

      const c1v =  resobj.v[resobj.classNames[0]].res[ncomp - 1];
      const xValues = c1v.yp;

      if (!resobj.ind) {
         const yValues = Vector.zeros(n).add(0.5);
         const lineColors = new Array(n).fill('#747573');
         const filled = new Array(n).fill(false);
         return {xValues, yValues, colors: lineColors, filled}
      }

      const yValues = Vector.zeros(n);
      const lineColors = new Array(n);
      const filled = new Array(n);

      let y = 0;
      for (let c of resobj.classNames) {
         const cmp = y === 0 ? (x, c) => x < c : (x, c) => x > c;
         const crit = model.crit ? model.crit[c] : 99999;
         const lineColor = CLASS_COLORS[y];
         for (let i of resobj.ind[c]) {
            yValues.v[i] = y + 1;
            lineColors[i] = lineColor;
            filled[i] = model.rule === 'hard' ? false : cmp(c1v.yp.v[i], crit[3]);
         }
         y = y + 1;
      }

      return {xValues, yValues, colors: lineColors, filled}
   }

   const series = $derived(getPoints(props.ncomp, resobj));

   // define size and width of marker
   const limX = $derived(series ? mrange(series.xValues) : [0, 1]);

   const marker = $derived(MARKERS[params.resID])
   const markerSize = $derived(marker == 1 ? [1.0, 0.8] : [0.8, 0.6])
   const markerWidth = $derived(marker == 1 ? [2.0, 1.2] : [2.0, 1.2])

   // name of the image and CSV files
   const fileName = 'plot-predictions';

   // the drawn size of the plot, for the class names cut to fit their legend cells; the
   // plot has no YAxis
   let size = $state([0, 0]);
   const legendLabels = $derived(classNames ?
      legendNames([classNames[1], classNames[0]], size, MARGINS, false) : null);

   // method for creating CSV version of the plot data, with the vertical lines as drawn: the
   // threshold for the hard rule, the boundaries of the classes shown for the soft one
   function downloadCSVCallback() {
      const { sep, dec } = props.data[params.resID];
      const classes = getClasses(resobj);
      let out = csvRow(['Set', 'Labels', 'Classes', 'Predicted y-values'], sep, dec);
      for (let i = 0; i < labels.length; i++) {
         out += csvRow([params.resID, labels[i], classes[i], series.xValues.v[i]], sep, dec);
      }
      if (model.rule === 'hard') {
         out += csvRow(['threshold', '', '', model.threshold], sep, dec);
      } else if (dist) {
         for (const c of classNames) {
            if (params.className !== c && params.className !== 'both') continue;
            out += csvRow([c + ' boundary', '', '', model.crit[c][2]], sep, dec);
            if (!params.showOutliers) continue;
            out += csvRow([c + ' outlier boundary', '', '', model.crit[c][3]], sep, dec);
         }
      }
      downloadCSV(out, fileName);
   }
</script>

{#snippet showOut(cnum)}
   {@const c = classNames[cnum]}
   {@const crit = model.crit[c]}

   {#if cnum === 0}
   <Rectangles
      top={[2.6]} left={[crit[3]]} height={[2.6]} width={[limX[1] - crit[3]]}
      faceColor={dist[c].lineColor + "10"} lineColor="transparent"
   />
   {:else}
   <Rectangles
      top={[2.6]} left={[limX[0]]} height={[2.6]} width={[crit[3] - limX[0]]}
      faceColor={dist[c].lineColor + "10"} lineColor="transparent"
   />
   {/if}

   <Lines xValues={dist[c].xValues} yValues={dist[c].yValues} lineColor={dist[c].lineColor}/>
   <Lines
      xValues={[crit[3], crit[3]]}
      yValues={[0, 2.6]}
      lineColor={dist[c].lineColor + "80"}
      lineType={3}
   />
{/snippet}


{#snippet showDist(cnum)}
   {@const c = classNames[cnum]}
   {@const crit = model.crit[c]}

   {#if cnum === 0}
   <Rectangles
      top={[2.6]} left={[crit[2]]} height={[2.6]} width={[limX[1] - crit[2]]}
      faceColor={dist[c].lineColor + "20"} lineColor="transparent"
   />
   {:else}
   <Rectangles
      top={[2.6]} left={[limX[0]]} height={[2.6]} width={[crit[2] - limX[0]]}
      faceColor={dist[c].lineColor + "20"} lineColor="transparent"
   />
   {/if}

   <Lines xValues={dist[c].xValues} yValues={dist[c].yValues} lineColor={dist[c].lineColor}/>
   <Lines
      xValues={[crit[2], crit[2]]}
      yValues={[0, 2.6]}
      lineColor={dist[c].lineColor}
      lineType={2}
   />
{/snippet}

{#if series }
<!-- 'display: contents' leaves the layout to Axes -->
<div style="display: contents" {@attach plotSize(v => size = v)}>
<Axes {title} {limX} {limY} {fileName}
   plotActions={[{text: '↓ csv', callback: downloadCSVCallback}]}
   downloadLinks="hover"
   margins={MARGINS}
   pngWidth={PNG_WIDTH}
   pngHeight={PNG_HEIGHT}
   pngRes={PNG_RES}
   >


   {#if model.rule === 'hard'}

      <Lines yValues={[0, 2.6]} xValues={[model.threshold, model.threshold]} lineType={2}
         lineColor={params.className === 'both' ? "#909090" : CLASS_COLORS[classNames.findIndex(v => v === params.className)]} />

      {#if params.className === classNames[0] || params.className  === "both"}
      <Rectangles
         top={[2.6]} left={[model.threshold]} height={[2.6]} width={[limX[1] - model.threshold]}
         faceColor={CLASS_COLORS[0] + "20"} lineColor="transparent"
      />
      {/if}

      {#if params.className === classNames[1] || params.className  === "both"}
      <Rectangles
         top={[2.6]} left={[limX[0]]} height={[2.6]} width={[model.threshold - limX[0]]}
         faceColor={CLASS_COLORS[1] + "20"} lineColor="transparent"
      />
      {/if}

   {:else if dist}

      {#if params.className === classNames[0] || params.className  === "both"}
         {#if params.showOutliers}{@render showOut(0)}{/if}
         {@render showDist(0)}
      {/if}

      {#if params.className === classNames[1] || params.className  === "both"}
         {#if params.showOutliers}{@render showOut(1)}{/if}
         {@render showDist(1)}
      {/if}

   {/if}


   <PlotSeries
      {...series}
      {labels}
      {selected}
      {callbacks}
      marker={marker}
      showLabels={params.showLabels !== 'hide'}
      markerSize={markerSize[nObj > 100 ? 1 : 0]}
      markerWidth={markerWidth[nObj > 100 ? 1 : 0]}
      plotType='p'
   />

   {#if classNames}
    <ColormapLegend colmap={[CLASS_COLORS[1], CLASS_COLORS[0]]} breaks={[0, 1, 2]} labels={legendLabels}/>
   {/if}

   <XAxis label="Predicted y-values" showGrid={true} />
   <Box/>
</Axes>
</div>
{/if}